import {MmProjectLoaderService} from '../../mm-project/mm-project-loader.service';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {filter, map, switchMap} from 'rxjs/operators';
import {flattenStructure, Structure} from '@docma-mm-core/structure/structure';
import {AddressTypeEnum, StructureAddress} from '@docma-mm-core/address/structure-address';
import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';

@Injectable()
export class StructureAddressesService {

  private structureAddressDictionary: Map<number, StructureAddress[]>;
  private hasAddressesSubject = new BehaviorSubject<boolean>(false);
  public hasAddresses$ = this.hasAddressesSubject.asObservable();

  public get StructureAddresses() {
    return this.structureAddressDictionary;
  }

  private structureAddressesSubject = new BehaviorSubject<Map<number, StructureAddress[]>>(null);
  public structureAddresses$ = this.structureAddressesSubject.asObservable();

  constructor(private readonly projectLoaderService: MmProjectLoaderService) {

    this.projectLoaderService.structures.pipe(filter(s => s?.loadState?.status === LoadStatusEnum.Loaded && s?.data?.length > 0),
      map(st => flattenStructure(st.data)),
      switchMap(structures =>
        this.projectLoaderService.structureAddresses.pipe(filter(s => s?.loadState?.status === LoadStatusEnum.Loaded && s?.data?.length > 0), map(a => a.data))
          .pipe(map(structureAddresses => ({structures, structureAddresses})))
      )
    ).subscribe(param => {
      if ((param.structures?.length ?? 0) >  0 && (param.structureAddresses?.length ?? 0) > 0)
      {
        this.structureAddressDictionary = buildStructureAddressDictionary(param.structures, param.structureAddresses);
        this.structureAddressesSubject.next(this.structureAddressDictionary);
      }

      this.hasAddressesSubject.next((param.structureAddresses?.length ?? 0) > 0);
    });

  }

}

export function buildStructureAddressDictionary(
  structures: Structure[],
  structureAddresses: StructureAddress[]
): Map<number, StructureAddress[]> {

  // ------------------------------------------------------------
  // 1. Group directly assigned addresses by Structure ID
  // ------------------------------------------------------------

  const directAddresses = new Map<
    number,
    Map<AddressTypeEnum, StructureAddress>
  >();

  for (const structureAddress of structureAddresses) {
    const structureId = structureAddress.Structure?.Id;

    if (structureId == null) {
      continue;
    }

    let addressesByType = directAddresses.get(structureId);

    if (!addressesByType) {
      addressesByType = new Map<AddressTypeEnum, StructureAddress>();
      directAddresses.set(structureId, addressesByType);
    }

    addressesByType.set(
      structureAddress.AddressType,
      structureAddress
    );
  }

  // ------------------------------------------------------------
  // 2. Sort structures by nested-set Low
  // ------------------------------------------------------------

  const sortedStructures = [...structures]
    .filter(s => s.Id != null && s.Low != null && s.High != null)
    .sort((a, b) => a.Low! - b.Low!);

  // ------------------------------------------------------------
  // 3. Build hierarchy from Low / High
  // ------------------------------------------------------------

  const childrenByParentId = new Map<number, Structure[]>();
  const roots: Structure[] = [];
  const stack: Structure[] = [];

  for (const structure of sortedStructures) {
    while (
      stack.length > 0 &&
      stack[stack.length - 1].High! < structure.Low!
      ) {
      stack.pop();
    }

    if (stack.length === 0) {
      roots.push(structure);
    } else {
      const parent = stack[stack.length - 1];

      let children = childrenByParentId.get(parent.Id!);

      if (!children) {
        children = [];
        childrenByParentId.set(parent.Id!, children);
      }

      children.push(structure);
    }

    stack.push(structure);
  }

  // ------------------------------------------------------------
  // 4. Propagate addresses
  // ------------------------------------------------------------

  const result = new Map<number, StructureAddress[]>();

  const visit = (
    structure: Structure,
    inheritedAddresses: Map<AddressTypeEnum, StructureAddress>
  ): void => {

    const currentAddresses = new Map(inheritedAddresses);

    const direct = directAddresses.get(structure.Id!);

    if (direct) {
      for (const [addressType, address] of direct) {
        currentAddresses.set(addressType, address);
      }
    }

    // At most 3 because AddressType is the key.
    result.set(
      structure.Id!,
      Array.from(currentAddresses.values())
    );

    const children = childrenByParentId.get(structure.Id!);

    if (children) {
      for (const child of children) {
        visit(child, currentAddresses);
      }
    }
  };

  for (const root of roots) {
    visit(root, new Map());
  }

  return result;
}
